import React from 'react';
import { notification } from 'antd';
import styled from 'styled-components';
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  InfoCircleFilled,
  StopFilled,
} from '@ant-design/icons';
import { BREAKPOINTS } from '@app/styles/themes/constants';
import { ArgsProps, ConfigProps } from 'antd/lib/notification';

interface IconWrapperProps {
  $isOnlyTitle: boolean;
}

const IconWrapper = styled.div<IconWrapperProps>`
  font-size: ${(props) => (props.$isOnlyTitle ? '2rem' : '3rem')};
  line-height: 2rem;
`;

const EmptyDescription = styled.div`
  margin-top: -0.75rem;
`;

export type NotificationSeverity = 'success' | 'info' | 'warning' | 'error';
export type NotificationProps = ArgsProps;
type NotificationConfig = ConfigProps;
export type NotificationViewport = 'mobile' | 'tablet' | 'desktop';

const responsiveConfigByViewport: Record<
  NotificationViewport,
  NotificationConfig
> = {
  mobile: {
    placement: 'top',
    top: 12,
    bottom: 12,
    maxCount: 2,
  },
  tablet: {
    placement: 'topRight',
    top: 16,
    bottom: 16,
    maxCount: 3,
  },
  desktop: {
    placement: 'topRight',
    top: 24,
    bottom: 24,
    maxCount: 4,
  },
};

const iconBySeverity: Record<NotificationSeverity, React.ReactNode> = {
  success: <CheckCircleFilled className="success-icon" />,
  info: <InfoCircleFilled className="info-icon" />,
  warning: <ExclamationCircleFilled className="warning-icon" />,
  error: <StopFilled className="error-icon" />,
};

const buildNotificationConfig = (
  severity: NotificationSeverity,
  config: NotificationProps
): NotificationProps => {
  const hasDescription = Boolean(config.description);
  const className = [
    config.className,
    !hasDescription && 'notification-without-description',
  ]
    .filter(Boolean)
    .join(' ');

  return {
    ...config,
    icon: (
      <IconWrapper $isOnlyTitle={!hasDescription}>
        {iconBySeverity[severity]}
      </IconWrapper>
    ),
    message: (
      <div className={`title ${!hasDescription ? 'title-only' : ''}`}>
        {config.message}
      </div>
    ),
    description: hasDescription ? (
      <div className="description" style={{ whiteSpace: 'pre-wrap' }}>
        {config.description}
      </div>
    ) : (
      <EmptyDescription />
    ),
    className,
  };
};

const openNotification = (
  severity: NotificationSeverity,
  config: NotificationProps
): void => {
  notification[severity](buildNotificationConfig(severity, config));
};

const notifyMany = (
  severity: NotificationSeverity,
  messages: React.ReactNode[],
  config: Omit<NotificationProps, 'message'> = {}
): void => {
  messages.forEach((message, index) => {
    openNotification(severity, {
      ...config,
      key:
        config.key !== undefined && config.key !== null
          ? `${String(config.key)}-${index}`
          : undefined,
      message,
    });
  });
};

const closeNotification = (key: React.Key): void => {
  notification.close(String(key));
};

const destroyNotifications = (key?: React.Key): void => {
  if (key !== undefined && key !== null) {
    closeNotification(key);
    return;
  }

  notification.destroy();
};

const configureNotifications = (config: NotificationConfig): void => {
  notification.config(config);
};

const getViewportFromWindow = (): NotificationViewport => {
  if (typeof window === 'undefined') return 'desktop';

  if (window.innerWidth < BREAKPOINTS.md) {
    return 'mobile';
  }

  if (window.innerWidth < BREAKPOINTS.xl) {
    return 'tablet';
  }

  return 'desktop';
};

const getResponsiveConfig = (
  viewport: NotificationViewport = getViewportFromWindow()
): NotificationConfig => responsiveConfigByViewport[viewport];

const setViewport = (viewport: NotificationViewport): void => {
  configureNotifications(getResponsiveConfig(viewport));
};

export const notificationController = {
  notify: openNotification,
  notifyMany,
  success: (config: NotificationProps) => openNotification('success', config),
  info: (config: NotificationProps) => openNotification('info', config),
  warning: (config: NotificationProps) => openNotification('warning', config),
  error: (config: NotificationProps) => openNotification('error', config),
  close: closeNotification,
  destroy: destroyNotifications,
  configure: configureNotifications,
  getResponsiveConfig,
  setViewport,
};
